import React, { useState, useEffect } from 'react';
import { resolveChatApi } from '../../hooks/useChat';
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Avatar,
    Divider,
    Button,
    TextField,
    InputAdornment,
    IconButton,
    Chip,
    CircularProgress,
    Tooltip,
    Paper,
    Grid
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/PeopleOutline';
import SearchIcon from '@mui/icons-material/Search';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import SensorsIcon from '@mui/icons-material/Sensors';
import BusinessIcon from '@mui/icons-material/Business';
import BadgeIcon from '@mui/icons-material/Badge';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const DEPARTMENTS = ['All', 'Engineering', 'Sales', 'Marketing', 'HR', 'Admin'];

const PeopleView = ({ searchQuery = '', onStartChat, currentUser: propUser }) => {
    const [activeFilter, setActiveFilter] = useState('all');
    const [selectedDept, setSelectedDept] = useState('All');
    const [search, setSearch] = useState(searchQuery || '');
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    const currentUser = propUser || (() => {
        try {
            const u = localStorage.getItem('x2infotech_user');
            return u ? JSON.parse(u) : null;
        } catch (e) {
            return null;
        }
    })();

    const loadUsers = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('x2infotech_token');
            const apiUrl = resolveChatApi();
            const res = await fetch(`${apiUrl}?action=get_all_users`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await res.json();
            if (data.success && Array.isArray(data.users)) {
                setUsers(data.users);
            }
        } catch (err) {
            console.error("Failed to load users:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadUsers();
    }, []);

    const handleDirectChat = async (targetUser) => {
        try {
            const token = localStorage.getItem('x2infotech_token');
            const apiUrl = resolveChatApi();
            const res = await fetch(`${apiUrl}?action=create_or_get_direct_channel`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ target_user_id: targetUser.id })
            });
            const data = await res.json();
            if (data.success) {
                if (onStartChat) {
                    onStartChat(data.channel_id, targetUser);
                } else {
                    alert(`Direct chat channel #${data.channel?.name || targetUser.name} created! Switch to Chat/Teams tab to message.`);
                }
            } else {
                alert(data.message || 'Failed to start direct chat');
            }
        } catch (err) {
            alert('Failed to connect: ' + err.message);
        }
    };

    const handleQuickCall = (contact) => {
        const meetCode = 'meet-' + Math.random().toString(36).substring(2, 9);
        navigator.clipboard.writeText(meetCode);
        alert(`Meeting invitation code copied: ${meetCode}\nShare this with ${contact.name} to join in Meet tab!`);
    };

    const filteredUsers = users.filter(user => {
        const q = search.toLowerCase();
        const matchesQuery = user.name?.toLowerCase().includes(q) ||
            user.email?.toLowerCase().includes(q) ||
            user.department?.toLowerCase().includes(q) ||
            user.role_name?.toLowerCase().includes(q);

        if (!matchesQuery) return false;

        if (selectedDept !== 'All') {
            const deptLower = selectedDept.toLowerCase();
            const userDept = (user.department || '').toLowerCase();
            const userRole = (user.role_name || '').toLowerCase();
            if (!userDept.includes(deptLower) && !userRole.includes(deptLower)) {
                return false;
            }
        }

        return true;
    });

    return (
        <Box sx={{ flex: 1, display: 'flex', height: '100%', bgcolor: '#f5f5f5', overflow: 'hidden' }}>
            {/* People Sidebar */}
            <Box
                sx={{
                    width: 280,
                    borderRight: '1px solid #edebe9',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: '#ffffff'
                }}
            >
                <Box sx={{ p: '16px 20px', borderBottom: '1px solid #edebe9' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '17px', color: '#242424' }}>
                        People & Directory
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#616161' }}>
                        {users.length} verified company members
                    </Typography>
                </Box>

                <Box sx={{ p: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#616161', textTransform: 'uppercase', letterSpacing: '0.5px', px: 1, mb: 1, display: 'block' }}>
                        Departments
                    </Typography>
                    <List sx={{ p: 0 }}>
                        {DEPARTMENTS.map(dept => {
                            const isSelected = selectedDept === dept;
                            return (
                                <ListItem
                                    key={dept}
                                    button
                                    onClick={() => setSelectedDept(dept)}
                                    sx={{
                                        borderRadius: 1.5,
                                        mb: 0.5,
                                        bgcolor: isSelected ? '#edebe9' : 'transparent',
                                        border: isSelected ? '1px solid #d1d1d1' : '1px solid transparent',
                                        '&:hover': { bgcolor: isSelected ? '#edebe9' : '#f3f2f1' }
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                        <BusinessIcon sx={{ fontSize: 18, color: isSelected ? '#4b53bc' : '#616161' }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={dept}
                                        primaryTypographyProps={{
                                             variant: 'body2',
                                             fontWeight: isSelected ? 700 : 500,
                                             color: isSelected ? '#4b53bc' : '#242424'
                                        }}
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                </Box>

                {/* Current User Card at Bottom */}
                <Box sx={{ mt: 'auto', p: 2, borderTop: '1px solid #edebe9', bgcolor: '#f5f5f5' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{
                            width: 38,
                            height: 38,
                            bgcolor: '#4b53bc',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: 15
                        }}>
                            {currentUser?.name?.charAt(0).toUpperCase() || 'U'}
                        </Avatar>
                        <Box sx={{ overflow: 'hidden' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#242424' }} noWrap>
                                {currentUser?.name || 'Logged User'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#4b53bc', fontWeight: 600, display: 'block' }} noWrap>
                                {currentUser?.role_name || 'Team Member'}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Main Content Area */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {/* Search & Top Bar */}
                <Box sx={{ p: '14px 24px', borderBottom: '1px solid #edebe9', bgcolor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#242424' }}>
                            {selectedDept === 'All' ? 'All Employees & Staff' : `${selectedDept} Department`}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#616161' }}>
                            Showing {filteredUsers.length} members
                        </Typography>
                    </Box>

                    <TextField
                        size="small"
                        placeholder="Search name, role, email..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{
                            width: 280,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                                bgcolor: '#f5f5f5',
                                '& fieldset': { borderColor: '#edebe9' },
                                '&:hover fieldset': { borderColor: '#8a8886' },
                                '&.Mui-focused fieldset': { borderColor: '#4b53bc' }
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 18, color: '#616161' }} />
                                </InputAdornment>
                            ),
                            style: { fontSize: '13px' }
                        }}
                    />
                </Box>

                {/* Directory List */}
                <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CircularProgress sx={{ color: '#4b53bc' }} />
                        </Box>
                    ) : filteredUsers.length > 0 ? (
                        <Grid container spacing={2}>
                            {filteredUsers.map((user) => {
                                const isSelf = user.id === currentUser?.id;
                                return (
                                    <Grid item xs={12} sm={6} md={4} key={user.id}>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                p: 2.5,
                                                borderRadius: 2,
                                                borderColor: '#edebe9',
                                                bgcolor: '#ffffff',
                                                boxShadow: 'none',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    borderColor: '#4b53bc',
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                                                <Avatar
                                                    sx={{
                                                        width: 48,
                                                        height: 48,
                                                        borderRadius: 1.5,
                                                        bgcolor: '#4b53bc',
                                                        color: '#ffffff',
                                                        fontWeight: 700,
                                                        fontSize: 18
                                                    }}
                                                >
                                                    {user.name?.charAt(0).toUpperCase() || 'U'}
                                                </Avatar>
                                                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#242424', fontSize: '15px' }} noWrap>
                                                        {user.name} {isSelf && '(You)'}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#616161', display: 'block' }} noWrap>
                                                        {user.email}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 0.8, mt: 1, flexWrap: 'wrap' }}>
                                                        <Chip
                                                            size="small"
                                                            label={user.department || 'General'}
                                                            sx={{ height: 20, fontSize: '10px', bgcolor: '#edebe9', color: '#4b53bc', fontWeight: 600 }}
                                                        />
                                                        <Chip
                                                            size="small"
                                                            label={user.role_name || 'Staff'}
                                                            sx={{ height: 20, fontSize: '10px', bgcolor: '#f3f2f1', color: '#616161', fontWeight: 600 }}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Box>

                                            <Divider sx={{ my: 1.5, borderColor: '#edebe9' }} />

                                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                {!isSelf && (
                                                    <>
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />}
                                                            onClick={() => handleDirectChat(user)}
                                                            sx={{
                                                                bgcolor: '#4b53bc',
                                                                color: '#fff',
                                                                textTransform: 'none',
                                                                fontWeight: 600,
                                                                fontSize: '12px',
                                                                py: 0.5,
                                                                boxShadow: 'none',
                                                                '&:hover': {
                                                                    bgcolor: '#3b43a2'
                                                                }
                                                            }}
                                                        >
                                                            Chat
                                                        </Button>

                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            startIcon={<VideocamOutlinedIcon sx={{ fontSize: 16 }} />}
                                                            onClick={() => handleQuickCall(user)}
                                                            sx={{
                                                                borderColor: '#edebe9',
                                                                color: '#4b53bc',
                                                                textTransform: 'none',
                                                                fontWeight: 600,
                                                                fontSize: '12px',
                                                                py: 0.5,
                                                                '&:hover': { borderColor: '#4b53bc', bgcolor: 'rgba(75, 83, 188, 0.08)' }
                                                            }}
                                                        >
                                                            Call
                                                        </Button>
                                                    </>
                                                )}
                                                {isSelf && (
                                                    <Typography variant="caption" sx={{ color: '#8a8886', fontStyle: 'italic', py: 0.5 }}>
                                                        Your profile account
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Paper>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    ) : (
                        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, textAlign: 'center' }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#242424' }}>
                                No employees found
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#616161' }}>
                                Try searching for another name or department filter.
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default PeopleView;
